import useSymbol from '@/hooks/use-symbol';
import { cn } from '@/lib/utils';
import { COLLATERAL_SYMBOL, useMarketStore } from '@/store/use-markets-store';
import {
  bigIntToDecimalStr,
  bigIntToNum,
  formatBigInt,
} from '@/utils/value-format';
import { motion, useAnimation } from 'framer-motion';
import { useEffect } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

type Props = {
  type: 'bid' | 'ask';
  unit: string;
  decimals: number;
  index: number;
  tickSize: string;
};

export default function OrderBookRow(props: Props) {
  const symbol = useSymbol();
  const hasCapacity = useStore(
    useMarketStore(symbol),
    (state) =>
      props.index <
      (props.tickSize in state.books
        ? state.books[props.tickSize]
        : { bids: [], asks: [] })[`${props.type}s`].length,
  );

  if (!hasCapacity) return;
  return <OrderBookRowBody {...props} />;
}

const OrderBookRowBody = ({ unit, type, index, decimals, tickSize }: Props) => {
  const symbol = useSymbol();

  const [price, size] = useStore(
    useMarketStore(symbol),
    useShallow(
      (state) =>
        (tickSize in state.books
          ? state.books[tickSize]
          : { bids: [], asks: [] })[`${type}s`][index],
    ),
  );

  const total = useStore(useMarketStore(symbol), (state) => {
    const totalSize = (
      tickSize in state.books ? state.books[tickSize] : { bids: [], asks: [] }
    )[`${type}s`]
      .slice(0, index + 1)
      .reduce((sum, [_, size]) => sum + size, 0n);
    let total: bigint;
    let decimals: bigint;
    switch (unit) {
      case COLLATERAL_SYMBOL:
        total = totalSize * state.marketData.midPrice;
        decimals =
          state.marketSpec.sizeDecimals + state.marketSpec.priceDecimals;
        break;
      default:
        total = totalSize;
        decimals = state.marketSpec.sizeDecimals;
        break;
    }
    return formatBigInt(total, decimals, {
      digits:
        unit === COLLATERAL_SYMBOL ? 2 : Number(state.marketSpec.sizeDecimals),
      compact: true,
    });
  });

  const unitSize = useStore(useMarketStore(symbol), (state) => {
    let total: bigint;
    let decimals: bigint;
    switch (unit) {
      case COLLATERAL_SYMBOL:
        total = size * state.marketData.midPrice;
        decimals =
          state.marketSpec.sizeDecimals + state.marketSpec.priceDecimals;
        break;
      default:
        total = size;
        decimals = state.marketSpec.sizeDecimals;
        break;
    }
    return formatBigInt(total, decimals, {
      digits:
        unit === COLLATERAL_SYMBOL ? 2 : Number(state.marketSpec.sizeDecimals),
      compact: true,
    });
  });

  const priceDecimals = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec.priceDecimals,
  );

  const onPriceChange = () => {
    document.getElementById('limit-order-tab')?.focus();

    window.setTimeout(() => {
      const priceInput = document.getElementById('price-input');

      if (!priceInput) return;

      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )?.set;

      if (!nativeInputValueSetter) return;

      // Set the value using the native setter
      nativeInputValueSetter.call(
        priceInput,
        bigIntToDecimalStr(price, priceDecimals),
      );

      const event = new Event('input', { bubbles: true });
      priceInput.dispatchEvent(event);
    }, 5);
  };

  return (
    <tr className="relative [&>td:first-child]:pl-4 [&>td:first-child]:text-left [&>td:last-child]:pr-4 [&>td]:text-right">
      <td
        onClick={onPriceChange}
        role="button"
        className={cn(
          'cursor-pointer font-mono hover:font-bold',
          type === 'bid' ? 'text-green' : 'text-red',
        )}
      >
        {formatBigInt(price, priceDecimals, { digits: decimals })}
      </td>
      <td className="font-mono">{unitSize}</td>
      <td className="relative font-mono">{total}</td>
    </tr>
  );
};

interface OrderBookBarProps extends Pick<Props, 'index' | 'type' | 'tickSize'> {
  numberOfRows: number;
}

export function OrderBookBar(props: OrderBookBarProps) {
  const symbol = useSymbol();
  const hasCapacity = useStore(
    useMarketStore(symbol),
    (state) =>
      props.index <
      (props.tickSize in state.books
        ? state.books[props.tickSize]
        : { bids: [], asks: [] })[`${props.type}s`].length,
  );

  if (!hasCapacity) return;
  return <OrderBookBarBody {...props} />;
}

export function OrderBookBarBody({
  index,
  type,
  numberOfRows,
  tickSize,
}: OrderBookBarProps) {
  const symbol = useSymbol();

  const animation = useAnimation();

  const [price, size] = useStore(
    useMarketStore(symbol),
    useShallow(
      (state) =>
        (tickSize in state.books
          ? state.books[tickSize]
          : { bids: [], asks: [] })[`${type}s`][index],
    ),
  );

  const total = useStore(useMarketStore(symbol), (state) => {
    const total = (
      tickSize in state.books ? state.books[tickSize] : { bids: [], asks: [] }
    )[`${type}s`]
      .slice(0, index + 1)
      .reduce((sum, [_, size]) => sum + size, 0n);
    return bigIntToNum(total, state.marketSpec.sizeDecimals);
  });

  const maxTotal = useStore(useMarketStore(symbol), (state) => {
    const { asks, bids } =
      tickSize in state.books ? state.books[tickSize] : { bids: [], asks: [] };
    const maxTotalBids = bids
      .slice(0, numberOfRows)
      .reduce((prev, curr) => prev + curr[1], 0n);
    const maxTotalAsks = asks
      .slice(0, numberOfRows)
      .reduce((prev, curr) => prev + curr[1], 0n);
    const maxTotal = maxTotalAsks > maxTotalBids ? maxTotalAsks : maxTotalBids;
    return bigIntToNum(maxTotal, state.marketSpec.sizeDecimals);
  });

  useEffect(() => {
    // @ts-ignore
    animation.start({
      opacity: [0.2, 0.1],
      transition: [
        { duration: 0.5, ease: 'easeOut' },
        { duration: 2, ease: 'easeOut' },
      ],
    });
  }, [price, size]);

  return (
    <motion.div
      animate={animation}
      className={cn(
        'h-[20px] opacity-10',
        type === 'ask' ? 'bg-red' : 'bg-green',
      )}
      style={{ width: `${(total / maxTotal) * 100}%` }}
    />
  );
}
